import type { MenuViewKey, Role, ViewKey } from './role';

/**
 * The accountant role's own three screens, in the order its menu shows them:
 * what the role is given to begin with, and the screens whose calls the
 * server's gate knows.
 *
 * The request of 5 October 2026 (third), section 10, locked the role to these
 * three: its menu showed Payments, Items and Archive and nothing else, and
 * every other page was blocked for it on the server. The request of
 * 6 October 2026 (fourth) took the lock away, in the client's words: "Allow
 * the admin to modify accounts like other users, it is not needed to be
 * locked." So the three are a default and not a limit. An accountant's screens
 * are whatever the access matrix and a person's own exceptions grant, as every
 * other role's are; nothing refuses a grant, and no cell of the two screens
 * that edit grants is locked for Accounts.
 *
 * Two things are left of section 10. The order: the three come first in an
 * accountant's menu, those of them that are held, and anything else after
 * (`MENU_ORDER` in the matrix). And the gate, while it is true to the screens
 * held: see `confinedToAccounts`.
 */
export const ACCOUNTS_VIEWS: readonly MenuViewKey[] = ['payments', 'items', 'archive'];

/**
 * The screens that ask the server for nothing of their own. The Dashboard is a
 * person's own page of widgets, and each widget reads what one of the person's
 * other screens already opens: no call belongs to the Dashboard. So holding it
 * changes nothing about the gate below, and it stands first in an accountant's
 * menu, because it is where they land. The lab's request of 12 October 2026,
 * and the API's ScreenAccess.WithoutCalls.
 */
export const SCREENS_WITHOUT_CALLS: readonly MenuViewKey[] = ['dashboard'];

/** The refusal of the gate, worded once for the mock server and the tests. The API sends the same. */
export const SCREEN_ACCESS_REFUSAL = {
  /** A call that belongs to no screen the caller holds. */
  outside: 'You do not have access to that part of the system.',
} as const;

/**
 * Whether the gate applies to this person: an accountant every one of whose
 * screens is one of the three, which an accountant with no screen at all also
 * is. The request of 6 October 2026 (fourth). The server's ScreenAccess.Confined.
 *
 * A confined accountant is answered through the server's list of what the
 * three screens call: anything else is refused, 403 "You do not have access to
 * that part of the system.", whatever permission they hold. That is the
 * accountant as the lab starts with one, and nothing about them has changed.
 *
 * One screen outside the three, however it was granted, to the role in the
 * matrix or to the person on their own record, and they are not confined: they
 * are answered like every other role, by each action's own checks alone. The
 * list knows the calls of three screens and no others, so going on asking it
 * would put the new screen in the menu and then refuse everything it asks for.
 *
 * Nobody of another role is ever confined, whatever they hold, the three alone
 * included; nor is anybody before their session has loaded.
 *
 * A screen that asks for nothing of its own is not a screen outside the three:
 * the Dashboard beside them leaves an accountant confined.
 */
export function confinedToAccounts(role: Role | undefined, views: readonly ViewKey[]): boolean {
  const own: readonly ViewKey[] = [...ACCOUNTS_VIEWS, ...SCREENS_WITHOUT_CALLS];
  return role === 'accountant' && views.every((view) => own.includes(view));
}

/**
 * Whether this person may write a Lab Note. A confined accountant reads a
 * case's notes and marks them read, and the server refuses the post, which is
 * in none of the three screens' calls, so the composer is not offered. An
 * accountant who holds another screen writes like anybody else, as the server
 * now allows: the request of 6 October 2026 (fourth).
 */
export function mayPostLabNote(role: Role | undefined, views: readonly ViewKey[]): boolean {
  return !confinedToAccounts(role, views);
}
